import React, { useCallback, useMemo, useState } from 'react'
import {
	Stack,
	Paper,
	Box,
	Typography,
	Divider,
	Grid,
	TextField,
	Button,
	MenuItem,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { Straighten, Save, Engineering, Schedule, Notes as NotesIcon } from '@mui/icons-material'
import { showErrorToast, showSuccessToast } from '../../services/CommonServices/alertServices'
import {
	getFormDefinition,
	listFormDefinitions,
	submitFormSubmission,
} from '../../services/formDefinitionsService'
import type { FormDefinitionDetail, FormField } from '../../types/Form/FormDefinition'
import type { FormSubmissionRequest } from '../../types/Form/FormSubmission'
import {
	buildRequiredFieldErrors,
	buildOrderedDynamicFormSections,
	buildSubmissionValues,
	sortFormFieldOptions,
} from '../../utils/dynamicFormsUtils'
import { ScaffoldEstimation } from '../../types/Scaffold/Scaffold'

const SCAFFOLD_ESTIMATION_CODE = 'scaffold_estimation'

/** Canonical section order for scaffold_estimation (extra sections render after these). */
const ESTIMATION_SECTION_ORDER = ['Dimensions', 'Scaffold Details', 'Estimation Notes'] as const

type FieldValues = Record<string, string>
type FieldErrors = Record<string, string | undefined>

interface EstimationFormProps {
	scaffoldRequestId: number
	refreshData: () => Promise<void>
	readOnly?: boolean
	initialEstimation?: ScaffoldEstimation
	initialExtraFields?: Record<string, string | null>
	estimatedBy?: string
}

export default function EstimationForm({
	scaffoldRequestId,
	refreshData,
	readOnly = false,
	initialEstimation,
	initialExtraFields,
	estimatedBy,
}: EstimationFormProps) {
	const [isEditing, setIsEditing] = useState(!readOnly)
	const [loading, setLoading] = useState(false)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [estimationForm, setEstimationForm] = useState<FormDefinitionDetail | null>(null)
	const [values, setValues] = useState<FieldValues>(() => {
		const out: FieldValues = {}
		const e = initialEstimation
		if (e) {
			out.length = String(e.length ?? '')
			out.width = String(e.width ?? '')
			out.height = String(e.height ?? '')
			out.estimatedDurationDays = String(e.estimatedDurationDays ?? '')
			out.numberOfLegs = String(e.numberOfLegs ?? '')
			out.numberOfDecks = String(e.numberOfDecks ?? '')
			out.laborRequired = String(e.laborRequired ?? '')
			out.notes = String(e.notes ?? '')
		}
		if (initialExtraFields) {
			for (const [k, v] of Object.entries(initialExtraFields)) {
				if (!k) continue
				// Only set when not already provided by the core estimation values.
				if (out[k] === undefined) out[k] = v ?? ''
			}
		}
		return out
	})
	const [submitting, setSubmitting] = useState(false)
	const [errors, setErrors] = useState<FieldErrors>({})

	const ensureDefaults = useCallback((formDetail: FormDefinitionDetail | null) => {
		if (!formDetail?.fields?.length) return
		setValues((prev) => {
			const next = { ...prev }
			for (const f of formDetail.fields) {
				if (f.isHidden) continue
				if (next[f.fieldKey] === undefined) next[f.fieldKey] = ''
			}
			return next
		})
	}, [])

	const loadForm = useCallback(async () => {
		setLoading(true)
		setLoadError(null)
		try {
			const list = await listFormDefinitions()
			const summary = list.find((f) => f.code === SCAFFOLD_ESTIMATION_CODE)
			if (!summary) {
				setEstimationForm(null)
				setLoadError(`Missing form configuration for ${SCAFFOLD_ESTIMATION_CODE}.`)
				return
			}
			const detail = await getFormDefinition(summary.id)
			setEstimationForm(detail)
		} catch {
			setEstimationForm(null)
			setLoadError('Could not load form configuration.')
		} finally {
			setLoading(false)
		}
	}, [])

	React.useEffect(() => {
		loadForm()
	}, [loadForm])

	React.useEffect(() => {
		ensureDefaults(estimationForm)
	}, [estimationForm, ensureDefaults])

	const setField = (key: string, value: string) => {
		setValues((p) => ({ ...p, [key]: value }))
		if (errors[key]) setErrors((p) => ({ ...p, [key]: undefined }))
	}

	const orderedSections = useMemo(
		() =>
			estimationForm
				? buildOrderedDynamicFormSections(estimationForm, ESTIMATION_SECTION_ORDER)
				: [],
		[estimationForm],
	)

	const placeholderProp = (f: FormField) => {
		const p = f.placeholder?.trim()
		return p ? { placeholder: p } : {}
	}

	const renderDynamicField = (
		f: FormField,
		opts: { hideLabel?: boolean; multilineOverride?: boolean } = {},
	) => {
		const value = values[f.fieldKey] ?? ''
		const common = {
			label: opts.hideLabel ? undefined : f.label,
			size: 'small' as const,
			fullWidth: true,
			required: f.isRequired,
			helperText: errors[f.fieldKey] ?? f.helpText ?? undefined,
			...placeholderProp(f),
			error: Boolean(errors[f.fieldKey]),
			disabled: readOnly || !isEditing,
			variant: 'outlined' as const,
			sx: {
				'& .MuiOutlinedInput-root': {
					borderRadius: 1,
					bgcolor: 'background.paper',
				},
				'& .MuiOutlinedInput-input': {
					py: 0.875,
					fontSize: '0.875rem',
				},
				'& .MuiInputBase-inputMultiline': {
					fontSize: '0.875rem',
				},
				// Read-only / submitted: keep values legible as “data shown”, not faded disabled chrome.
				...(readOnly && {
					'& .MuiOutlinedInput-root.Mui-disabled': {
						bgcolor: 'grey.100',
					},
					'& .MuiOutlinedInput-input.Mui-disabled, & .MuiOutlinedInput-input.MuiInputBase-inputDisabled, & textarea.Mui-disabled':
						{
							color: 'text.primary',
							WebkitTextFillColor: (theme: Theme) => theme.palette.text.primary,
							opacity: 1,
						},
					'& .MuiSelect-select.Mui-disabled': {
						color: 'text.primary',
						WebkitTextFillColor: (theme: Theme) => theme.palette.text.primary,
						opacity: 1,
					},
					'& .MuiSelect-icon.Mui-disabled': { opacity: 0.65 },
					'& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
						borderColor: 'divider',
					},
					'& .MuiFormLabel-root.Mui-disabled': {
						color: 'text.secondary',
						opacity: 1,
					},
				}),
			},
		}

		if (f.fieldType === 'dropdown') {
			const optsList = sortFormFieldOptions(f)
			return (
				<TextField
					key={f.id}
					select
					{...common}
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					InputLabelProps={{ shrink: true }}
					SelectProps={{ displayEmpty: !f.isRequired }}
				>
					{!f.isRequired && <MenuItem value=''>None</MenuItem>}
					{optsList.length === 0 ? (
						<MenuItem value='' disabled>
							No options configured
						</MenuItem>
					) : (
						optsList.map((o) => (
							<MenuItem key={o.id} value={o.value}>
								{o.value}
							</MenuItem>
						))
					)}
				</TextField>
			)
		}

		if (f.fieldType === 'number' || f.fieldType === 'currency') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='number'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					inputProps={f.fieldType === 'currency' ? { step: '0.01' } : { step: 'any' }}
				/>
			)
		}

		if (f.fieldType === 'date') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='date'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>
			)
		}

		const multiline =
			opts.multilineOverride === true ||
			(opts.multilineOverride !== false &&
				(f.fieldKey.toLowerCase() === 'notes' || f.fieldType === 'textarea'))
		return (
			<TextField
				key={f.id}
				{...common}
				value={value}
				onChange={(e) => setField(f.fieldKey, e.target.value)}
				multiline={multiline}
				minRows={multiline ? 3 : undefined}
			/>
		)
	}

	const validateForm = (): boolean => {
		if (!estimationForm) return false
		const next: FieldErrors = buildRequiredFieldErrors(estimationForm, values)

		// Required numeric/currency fields must be > 0 (matches NewRequestDialog / prior UX).
		for (const f of estimationForm.fields) {
			if (f.isHidden || !f.isRequired) continue
			if (f.fieldType !== 'number' && f.fieldType !== 'currency') continue
			const k = f.fieldKey
			if (next[k]) continue
			const raw = (values[k] ?? '').trim()
			if (!raw) {
				next[k] = 'This field is required'
				continue
			}
			const n = Number(raw)
			if (!Number.isFinite(n) || n <= 0) {
				next[k] = 'Must be greater than 0'
			}
		}

		setErrors(next)
		return Object.keys(next).length === 0
	}

	const handleSave = async () => {
		if (validateForm()) {
			setSubmitting(true)
			try {
				if (!estimationForm) return

				let payload: FormSubmissionRequest['values'] = []
				try {
					payload = buildSubmissionValues(estimationForm, values)
				} catch (e: any) {
					showErrorToast({ message: e?.message || 'Invalid values.' })
					return
				}

				if (payload.length === 0) {
					showErrorToast({ message: 'Nothing to submit.' })
					return
				}

				await submitFormSubmission(estimationForm.id, {
					values: payload,
					specialFields: { scaffoldRequestId },
				})
				await refreshData()
				showSuccessToast('Estimation submitted successfully!', true)
			} finally {
				setSubmitting(false)
			}
			setIsEditing(false)
		}
	}

	const sectionHeaderIcon = (sectionName: string) => {
		switch (sectionName.trim()) {
			case 'Dimensions':
				return <Straighten fontSize='small' />
			case 'Scaffold Details':
				return <Schedule fontSize='small' />
			case 'Estimation Notes':
				return <NotesIcon fontSize='small' />
			default:
				return <Straighten fontSize='small' />
		}
	}

	const renderInlineFieldRow = (f: FormField, sectionName: string) => (
		<Box
			key={f.id}
			sx={{
				display: 'grid',
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
				columnGap: 2,
				rowGap: 1.25,
				alignItems: 'center',
				// Second column caps input width but can shrink so the row never overflows the grid cell.
				gridTemplateColumns: {
					xs: 'minmax(0, 1fr)',
					sm: 'minmax(0, 1fr) minmax(0, 180px)',
				},
			}}
		>
			<Typography
				variant='subtitle2'
				color='text.secondary'
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					minWidth: 0,
					overflow: 'hidden',
					fontWeight: 500,
					fontSize: '0.8125rem',
					letterSpacing: '0.01em',
					'& .MuiSvgIcon-root': { color: 'text.secondary', fontSize: '1.1rem' },
				}}
			>
				{sectionHeaderIcon(sectionName)}
				<Box component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
					{f.label}
				</Box>
			</Typography>
			<Box
				sx={{
					width: '100%',
					minWidth: 0,
					maxWidth: '100%',
					gridColumn: { xs: '1 / -1', sm: 'auto' },
				}}
			>
				{renderDynamicField(f, { hideLabel: true })}
			</Box>
		</Box>
	)

	const renderNotesFieldFullWidth = (f: FormField) => {
		const isNotesLike =
			f.fieldKey.toLowerCase() === 'notes' ||
			f.sectionName.trim() === 'Estimation Notes' ||
			f.fieldType === 'textarea'
		return (
			<Box key={f.id} sx={{ width: '100%' }}>
				{renderDynamicField(f, { hideLabel: true, multilineOverride: isNotesLike })}
			</Box>
		)
	}

	const renderSectionBlock = (sectionName: string, fields: FormField[]) => {
		if (!fields.length) return null
		const trimmed = sectionName.trim()
		const isNotes = trimmed === 'Estimation Notes'
		return (
			<Stack
				spacing={2}
				sx={{
					alignItems: isNotes ? 'stretch' : 'flex-start',
					width: '100%',
					minWidth: 0,
					maxWidth: '100%',
				}}
			>
				<Typography
					variant='subtitle1'
					sx={{
						fontWeight: 600,
						fontSize: '0.95rem',
						letterSpacing: '0.02em',
						color: 'text.primary',
						...(isNotes
							? {
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: 1,
									width: '100%',
									'& .MuiSvgIcon-root': { color: 'text.secondary' },
							  }
							: {}),
					}}
				>
					{isNotes ? sectionHeaderIcon(trimmed) : null}
					{trimmed}
				</Typography>
				{isNotes
					? fields.map((f) => renderNotesFieldFullWidth(f))
					: fields.map((f) => renderInlineFieldRow(f, trimmed))}
			</Stack>
		)
	}

	const dimensionsSection = orderedSections.find((s) => s.sectionName === 'Dimensions')
	const scaffoldDetailsSection = orderedSections.find((s) => s.sectionName === 'Scaffold Details')
	const estimationNotesSection = orderedSections.find((s) => s.sectionName === 'Estimation Notes')

	

	return (
		<Paper
			variant='outlined'
			sx={{
				p: 2.5,
				bgcolor: 'grey.50',
				borderColor: 'divider',
				borderWidth: 1,
				borderStyle: 'solid',
			}}
		>
			<Stack spacing={2.5}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography
						variant='h6'
						sx={{
							fontWeight: 600,
							color: 'primary.main',
							letterSpacing: '0.02em',
							fontSize: '1.05rem',
						}}
					>
						Scaffold Estimation
					</Typography>
				</Box>
				<Divider sx={{ borderColor: 'divider' }} />

				{loading && (
					<Typography variant='body2' color='text.secondary'>
						Loading estimation form...
					</Typography>
				)}
				{!loading && loadError && (
					<Typography variant='body2' color='error'>
						{loadError}
					</Typography>
				)}

				<Grid container spacing={2.5} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
					<Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
						{renderSectionBlock(dimensionsSection?.sectionName ?? '', dimensionsSection?.fields ?? [])}
					</Grid>

					<Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: '100%' }}>
						<Stack spacing={2.25} sx={{ alignItems: 'flex-start', width: '100%', minWidth: 0, maxWidth: '100%' }}>
							{renderSectionBlock(
								scaffoldDetailsSection?.sectionName ?? '',
								scaffoldDetailsSection?.fields ?? [],
							)}
							{estimatedBy ? (
								<Box
									sx={{
										display: 'grid',
										width: '100%',
										maxWidth: '100%',
										minWidth: 0,
										columnGap: 2,
										rowGap: 0.5,
										alignItems: 'center',
										gridTemplateColumns: {
											xs: 'minmax(0, 1fr)',
											sm: 'minmax(0, 1fr) minmax(0, 1fr)',
										},
									}}
								>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: 0,
											overflow: 'hidden',
											fontWeight: 500,
											fontSize: '0.8125rem',
											letterSpacing: '0.01em',
											'& .MuiSvgIcon-root': { color: 'text.secondary', fontSize: '1.1rem' },
										}}
									>
										<Engineering fontSize='small' />
										Estimated by:
									</Typography>
									<Typography
										variant='body1'
										sx={{
											fontWeight: 500,
											minWidth: 0,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											textAlign: { xs: 'left', sm: 'right' },
											fontSize: '0.875rem',
											color: 'text.primary',
										}}
									>
										{estimatedBy}
									</Typography>
								</Box>
							) : null}
						</Stack>
					</Grid>

					<Grid item xs={12} sx={{ minWidth: 0, maxWidth: '100%' }}>
						{renderSectionBlock(
							estimationNotesSection?.sectionName ?? '',
							estimationNotesSection?.fields ?? [],
						)}
					</Grid>
				</Grid>

				{/* Action Buttons */}
				{!readOnly && (
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2.5 }}>
						<Button
							variant='contained'
							color='primary'
							size='large'
							disabled={submitting || loading || Boolean(loadError) || !estimationForm}
							onClick={handleSave}
							startIcon={<Save />}
							sx={{
								textTransform: 'none',
								fontWeight: 600,
								px: 2.5,
								py: 1,
								borderRadius: 1,
							}}
						>
							{submitting ? 'Submitting...' : 'Submit'}
						</Button>
					</Box>
				)}
			</Stack>
		</Paper>
	)
}
